/**
 * Additional RMA fees picker — multi-select backed by the RmaFee entity slice.
 *
 * Replicates legacy RmaFeeSelectField behavior: a collapsible list of
 * non-obsolete fees that can be added one at a time. Selected fees are
 * shown with a remove action. The parent manages the selected id list
 * via `selected` (Immutable List of ids) + `onAdd` / `onRemove` callbacks.
 */

import { Box, IconButton, List, ListItem, ListItemText, MenuItem, Select, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { rmaFeeSelector } from '@sophocles/entities/RmaFee';

/**
 * @param {object} props
 * @param {import('immutable').List} props.selected  - List of fee ids
 * @param {function} props.onAdd    - (feeId) => void
 * @param {function} props.onRemove - (listIndex) => void
 */
export default function RmaFeeMultiSelect({ selected, onAdd, onRemove }) {
  const { t } = useTranslation();
  const raw = useSelector(rmaFeeSelector);
  const [open, setOpen] = useState(false);

  const options = raw
    .sortBy(e => e.get('id'))
    .toList()
    .filter(e => !e.get('obsolete', false))
    .map(e => ({
      id: e.get('id'),
      label: `${e.get('reason')} - $${e.get('fee')}`,
    }));

  const unselectedOptions = options.filter(o => !selected.includes(o.id + '') && !selected.includes(o.id));

  const getLabel = id => {
    const entry = options.find(o => o.id == id);
    return entry ? entry.label : t('rma.feeNumberShort', 'Fee #{{id}}', { id });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="body2" fontWeight="bold">
          {t('rma.additionalFees', 'Additional Fees')}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'primary.main', cursor: 'pointer' }}
          onClick={() => setOpen(prev => !prev)}
        >
          {open ? t('rma.close', '(close)') : t('rma.add', '(add)')}
        </Typography>
      </Box>

      {open && (
        <Select
          size="small"
          displayEmpty
          hiddenLabel
          value=""
          onChange={e => {
            if (e.target.value) {
              onAdd(e.target.value);
              setOpen(false);
            }
          }}
          sx={{ minWidth: 300, mb: 1 }}
        >
          <MenuItem value="" disabled>
            {t('rma.selectFeeToAdd', 'Select a fee to add…')}
          </MenuItem>
          {unselectedOptions.toArray().map(o => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      )}

      {selected.size === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('rma.noAdditionalFeesSelected', 'No additional fees selected')}
        </Typography>
      ) : (
        <List dense disablePadding>
          {selected.toArray().map((id, idx) => (
            <ListItem
              key={`${id}-${idx}`}
              disableGutters
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => onRemove(idx)}
                  aria-label={t('rma.removeFee', 'remove fee')}
                >
                  ✕
                </IconButton>
              }
            >
              <ListItemText primary={getLabel(id)} />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}
